/**
 * useGymOwners Hook
 *
 * Custom hook for fetching all owners of a specific gym.
 * Queries gymMemberships with role='owner' and joins with users collection
 * to provide display names.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import type { GymMembership, GymRole, User } from '@flash/shared';

/**
 * Convert Firestore document to GymMembership type
 */
const docToGymMembership = (docData: DocumentData): GymMembership => {
  return {
    id: docData['id'] as string,
    odKey: docData['odKey'] as string,
    userId: docData['userId'] as string,
    gymId: docData['gymId'] as string,
    role: docData['role'] as GymRole,
    isHomeGym: docData['isHomeGym'] as boolean,
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
  };
};

/**
 * Owner with user profile information
 */
interface GymOwner {
  /** The gym membership record */
  membership: GymMembership;
  /** User ID */
  userId: string;
  /** Display name from user profile */
  displayName: string;
  /** Email from user profile */
  email: string;
  /** Photo URL from user profile */
  photoURL: string | null;
}

/**
 * Options for useGymOwners hook
 */
interface UseGymOwnersOptions {
  /** Whether to use real-time listener (default: true) */
  realtime?: boolean;
}

/**
 * State interface for gym owners query
 */
interface UseGymOwnersState {
  /** List of gym owners with profile information */
  owners: GymOwner[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useGymOwners hook
 */
interface UseGymOwnersResult extends UseGymOwnersState {
  /** Refetch owners from Firestore */
  refetch: () => Promise<void>;
  /** Number of owners */
  ownerCount: number;
}

/**
 * Fetch user profile by ID
 */
const fetchUserProfile = async (userId: string): Promise<Partial<User> | null> => {
  try {
    const db = getFirebaseDb();
    const userRef = doc(db, 'users', userId);
    const userSnapshot = await getDoc(userRef);

    if (!userSnapshot.exists()) {
      return null;
    }

    const data = userSnapshot.data();
    return {
      id: userSnapshot.id,
      displayName: data['displayName'] as string,
      email: data['email'] as string,
      photoURL: (data['photoURL'] as string | null) ?? null,
    };
  } catch {
    return null;
  }
};

/**
 * Hook to fetch all owners of a specific gym
 *
 * @param gymId - The gym ID to fetch owners for (null to skip)
 * @param options - Optional configuration
 * @returns Object containing owners, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { owners, loading, ownerCount } = useGymOwners(gymId);
 *
 * if (loading) return <LoadingSpinner />;
 *
 * return (
 *   <div>
 *     <h3>Gym Owners ({ownerCount})</h3>
 *     {owners.map(owner => (
 *       <OwnerCard
 *         key={owner.userId}
 *         name={owner.displayName}
 *         email={owner.email}
 *         photoURL={owner.photoURL}
 *       />
 *     ))}
 *   </div>
 * );
 * ```
 */
export const useGymOwners = (
  gymId: string | null,
  options: UseGymOwnersOptions = {}
): UseGymOwnersResult => {
  const { realtime = true } = options;

  const [state, setState] = useState<UseGymOwnersState>({
    owners: [],
    loading: true,
    error: null,
  });

  /**
   * Fetch owner memberships and enrich with user profiles
   */
  const fetchOwners = useCallback(async (): Promise<void> => {
    if (!gymId) {
      setState({ owners: [], loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const db = getFirebaseDb();
      const membershipsRef = collection(db, 'gymMemberships');
      const membershipsQuery = query(
        membershipsRef,
        where('gymId', '==', gymId),
        where('role', '==', 'owner')
      );

      const snapshot = await getDocs(membershipsQuery);

      const memberships: GymMembership[] = snapshot.docs.map((docSnapshot) =>
        docToGymMembership(docSnapshot.data())
      );

      // Fetch user profiles for all owners
      const owners: GymOwner[] = await Promise.all(
        memberships.map(async (membership) => {
          const userProfile = await fetchUserProfile(membership.userId);
          return {
            membership,
            userId: membership.userId,
            displayName: userProfile?.displayName ?? 'Unknown User',
            email: userProfile?.email ?? '',
            photoURL: userProfile?.photoURL ?? null,
          };
        })
      );

      setState({ owners, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gym owners';
      console.error('Error fetching gym owners:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [gymId]);

  /**
   * Process memberships snapshot and enrich with user profiles
   */
  const processMemberships = useCallback(async (memberships: GymMembership[]): Promise<void> => {
    try {
      // Fetch user profiles for all owners
      const owners: GymOwner[] = await Promise.all(
        memberships.map(async (membership) => {
          const userProfile = await fetchUserProfile(membership.userId);
          return {
            membership,
            userId: membership.userId,
            displayName: userProfile?.displayName ?? 'Unknown User',
            email: userProfile?.email ?? '',
            photoURL: userProfile?.photoURL ?? null,
          };
        })
      );

      setState({ owners, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process gym owners';
      console.error('Error processing gym owners:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, []);

  // Real-time subscription effect
  useEffect(() => {
    if (!gymId) {
      setState({ owners: [], loading: false, error: null });
      return;
    }

    // For non-realtime mode, just fetch once
    if (!realtime) {
      fetchOwners();
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const membershipsRef = collection(db, 'gymMemberships');
    const membershipsQuery = query(
      membershipsRef,
      where('gymId', '==', gymId),
      where('role', '==', 'owner')
    );

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      membershipsQuery,
      async (snapshot) => {
        const memberships: GymMembership[] = snapshot.docs.map((docSnapshot) =>
          docToGymMembership(docSnapshot.data())
        );

        await processMemberships(memberships);
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gym owners';
        console.error('Error in gym owners subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [gymId, realtime, fetchOwners, processMemberships]);

  return {
    ...state,
    refetch: fetchOwners,
    ownerCount: state.owners.length,
  };
};
